// Shipping cost calculation utilities for ocean freight
// Based on FCL (Full Container Load) and LCL (Less than Container Load) rates

// Constants for shipping calculation
export const SHIPPING_RATES = {
  // FCL rates (per container)
  FCL_20FT: {
    base: 2500, // Base rate for 20ft container
    min: 2000,
    max: 4000,
  },
  FCL_40FT: {
    base: 4000, // Base rate for 40ft container
    min: 3500,
    max: 6000,
  },
  // LCL rates (per CBM)
  LCL: {
    perCBM: 65, // Average $50-80 per CBM
    min: 50,
    max: 80,
  },
  // Additional fees
  FUEL_SURCHARGE_PERCENT: 0.15, // 15% fuel surcharge
  PORT_FEES: 350, // Average port handling fees
  DOCUMENTATION_FEE: 150,
  INSURANCE_PERCENT: 0.02, // 2% of cargo value for insurance
};

// Container capacity
export const CONTAINER_CAPACITY = {
  '20ft': {
    maxWeight: 28000, // kg
    maxCBM: 33, // cubic meters
  },
  '40ft': {
    maxWeight: 26000, // kg (less due to container weight)
    maxCBM: 67, // cubic meters
  },
};

// Destination zones and modifiers
export const DESTINATION_ZONES = {
  'North America': { modifier: 1.0, transitDays: '25-35' },
  'Europe': { modifier: 1.1, transitDays: '30-40' },
  'Southeast Asia': { modifier: 0.6, transitDays: '10-15' },
  'Middle East': { modifier: 0.8, transitDays: '20-25' },
  'South America': { modifier: 1.3, transitDays: '35-45' },
  'Africa': { modifier: 1.4, transitDays: '40-50' },
  'Australia': { modifier: 0.9, transitDays: '20-30' },
  'Other': { modifier: 1.2, transitDays: '30-45' },
};

// Calculate dimensional weight (for LCL)
export const calculateDimensionalWeight = (lengthCm, widthCm, heightCm) => {
  // CBM calculation
  const cbm = (lengthCm * widthCm * heightCm) / 1000000;
  // Dimensional weight factor for ocean freight (typically 1 CBM = 1000 kg)
  const dimWeight = cbm * 1000;
  return { cbm, dimWeight };
};

// Determine shipping method recommendation
export const recommendShippingMethod = (weightKg, cbm) => {
  // If weight > 500kg or CBM > 1, consider ocean freight
  if (weightKg < 500 && cbm < 1) {
    return {
      method: 'AIR_FREIGHT',
      reason: 'Shipment under 500kg - air freight may be more economical',
    };
  }
  
  // Check if FCL is more economical than LCL
  // Rule of thumb: FCL becomes cheaper when cargo > 15 CBM
  if (cbm >= 15 || weightKg >= 10000) {
    if (cbm <= 33 && weightKg <= 28000) {
      return {
        method: 'FCL_20FT',
        reason: 'Full 20ft container recommended for this shipment size',
      };
    } else {
      return {
        method: 'FCL_40FT',
        reason: 'Full 40ft container recommended for this shipment size',
      };
    }
  }
  
  return {
    method: 'LCL',
    reason: 'Less than Container Load - shared container shipping',
  };
};

// Calculate shipping cost
export const calculateShippingCost = (engine, destination, options = {}) => {
  const {
    customWeight = null,
    customCBM = null,
    insuranceIncluded = true,
  } = options;

  // Get engine weight (estimate if not available)
  // Marine engines typically: 0.5-1.5 kg per HP for medium engines
  const estimatedWeight = engine.weight || (engine.horsepower * 0.8);
  const weightKg = customWeight || estimatedWeight;
  
  // Estimate dimensions based on HP (rough approximation)
  // Marine engines are roughly: length = HP * 0.5mm, width = HP * 0.3mm, height = HP * 0.3mm
  const estimatedLength = Math.min(400, Math.max(150, engine.horsepower * 0.15)); // cm
  const estimatedWidth = Math.min(200, Math.max(100, engine.horsepower * 0.1)); // cm
  const estimatedHeight = Math.min(200, Math.max(80, engine.horsepower * 0.08)); // cm
  
  const { cbm } = calculateDimensionalWeight(estimatedLength, estimatedWidth, estimatedHeight);
  const actualCBM = customCBM || cbm;
  
  // Get shipping method recommendation
  const recommendation = recommendShippingMethod(weightKg, actualCBM);
  
  // Get destination modifier
  const zone = DESTINATION_ZONES[destination] || DESTINATION_ZONES['Other'];
  
  let baseCost = 0;
  let containerType = null;
  
  switch (recommendation.method) {
    case 'FCL_20FT':
      baseCost = SHIPPING_RATES.FCL_20FT.base;
      containerType = '20ft Container';
      break;
    case 'FCL_40FT':
      baseCost = SHIPPING_RATES.FCL_40FT.base;
      containerType = '40ft Container';
      break;
    case 'LCL':
    default:
      baseCost = actualCBM * SHIPPING_RATES.LCL.perCBM;
      containerType = 'Shared Container (LCL)';
      break;
  }
  
  // Apply destination modifier
  baseCost *= zone.modifier;
  
  // Calculate additional fees
  const fuelSurcharge = baseCost * SHIPPING_RATES.FUEL_SURCHARGE_PERCENT;
  const portFees = SHIPPING_RATES.PORT_FEES;
  const documentationFee = SHIPPING_RATES.DOCUMENTATION_FEE;
  
  // Insurance (optional)
  const cargoValue = engine.price || 50000;
  const insuranceCost = insuranceIncluded ? (cargoValue * SHIPPING_RATES.INSURANCE_PERCENT) : 0;
  
  // Total
  const subtotal = baseCost + fuelSurcharge + portFees + documentationFee;
  const total = subtotal + insuranceCost;
  
  return {
    method: recommendation.method,
    methodDescription: recommendation.reason,
    containerType,
    destination,
    transitTime: zone.transitDays,
    breakdown: {
      baseFreight: Math.round(baseCost),
      fuelSurcharge: Math.round(fuelSurcharge),
      portFees,
      documentationFee,
      insurance: Math.round(insuranceCost),
    },
    subtotal: Math.round(subtotal),
    total: Math.round(total),
    estimatedWeight: Math.round(weightKg),
    estimatedCBM: actualCBM.toFixed(2),
    engineHP: engine.horsepower,
    notes: [
      `Estimated transit: ${zone.transitDays} days`,
      `Weight: ~${Math.round(weightKg).toLocaleString()} kg`,
      `Volume: ~${actualCBM.toFixed(2)} CBM`,
      insuranceIncluded ? 'Cargo insurance included' : 'Cargo insurance not included',
    ],
  };
};

// Get available destinations
export const getDestinations = () => {
  return Object.entries(DESTINATION_ZONES).map(([name, data]) => ({
    name,
    ...data,
  }));
};
